import { useMemo } from 'react';
import { Icon } from '@iconify/react';
import { Button } from '@/components/shadcn/button';
import { Card } from '@/components/shadcn/card';
import type {
  BadmintonGameListItem,
  BadmintonStandingItem,
  BadmintonTopMmrItem,
} from '@/services/badmintonService';
import type { FlatBadmintonGame } from '@/components/community/badminton/badmintonListUtils';
import BadmintonMatchCard from './BadmintonMatchCard';
import BadmintonStandingsTable from './BadmintonStandingsTable';
import BadmintonTopMmrList from './BadmintonTopMmrList';
import BadmintonStatistikTabs, { type BadmintonStatistikTab } from './BadmintonStatistikTabs';

interface BadmintonStatistikContentProps {
  tab: BadmintonStatistikTab;
  onTabChange: (tab: BadmintonStatistikTab) => void;
  canEdit: boolean;
  standings: BadmintonStandingItem[];
  topMmr: BadmintonTopMmrItem[];
  flatGames: FlatBadmintonGame[];
  onTambahGame: () => void;
  onEditScore: (item: FlatBadmintonGame) => void;
  onEditPlayer: (game: BadmintonGameListItem, gameNumber: number) => void;
  onDelete: (item: FlatBadmintonGame) => void;
  onShare?: () => void;
  onDownload?: () => void;
  downloading?: boolean;
}

export default function BadmintonStatistikContent({
  tab,
  onTabChange,
  canEdit,
  standings,
  topMmr,
  flatGames,
  onTambahGame,
  onEditScore,
  onEditPlayer,
  onDelete,
  onShare,
  onDownload,
  downloading,
}: BadmintonStatistikContentProps) {
  const normalizedStandings = useMemo(() => {
    return standings.map((item) => {
      const hasPoints = (item.pointsScored ?? 0) > 0 || (item.pointsConceded ?? 0) > 0;
      return {
        ...item,
        gamesPlayed: hasPoints ? item.gamesPlayed : 0,
        winRate: hasPoints ? item.winRate : 0,
        setsWon: hasPoints ? (item.setsWon ?? 0) : 0,
        setsLost: hasPoints ? (item.setsLost ?? 0) : 0,
      };
    });
  }, [standings]);

  return (
    <div className="space-y-4">
      <BadmintonStatistikTabs tab={tab} onTabChange={onTabChange} />

      {tab === 'games' && (
        <div className="space-y-3 pb-24">
          {flatGames.length === 0 ? (
            <Card className="rounded-2xl border-0 bg-white px-4 py-10 text-center text-sm text-gray-400 shadow-[inset_0_0_0_1px_#E0E6ED]">
              Belum ada pertandingan
            </Card>
          ) : (
            flatGames.map((item) => (
              <BadmintonMatchCard
                key={item.game.slug}
                game={item.game}
                gameNumber={item.gameNumber}
                canEdit={canEdit}
                onEditScore={() => onEditScore(item)}
                onEditPlayer={() => onEditPlayer(item.game, item.gameNumber)}
                onDelete={() => onDelete(item)}
              />
            ))
          )}
        </div>
      )}

      {tab === 'standings' && (
        <div className="space-y-3">
          <BadmintonStandingsTable standings={normalizedStandings} />

          {canEdit && normalizedStandings.length > 0 && (onShare || onDownload) && (
            <div className="flex items-center gap-3 pt-1">
              {onShare && (
                <button
                  type="button"
                  onClick={onShare}
                  className="flex-1 h-11 sm:h-12 rounded-2xl bg-[#FFF5EE] text-[#FC480D] font-bold text-xs sm:text-sm flex items-center justify-center gap-2 border border-[#FFEDD5] hover:bg-[#FFEAE0] active:scale-98 transition-all cursor-pointer"
                >
                  <Icon icon="ph:share-network" className="w-4 h-4 sm:w-5 sm:h-5 text-[#FC480D]" />
                  <span>Bagikan</span>
                </button>
              )}
              {onDownload && (
                <button
                  type="button"
                  onClick={onDownload}
                  disabled={downloading}
                  className="flex-1 h-11 sm:h-12 rounded-2xl bg-[#FC480D] hover:bg-[#D83B08] text-white font-bold text-xs sm:text-sm flex items-center justify-center gap-2 active:scale-98 transition-all shadow-sm cursor-pointer disabled:opacity-60"
                >
                  {downloading ? (
                    <>
                      <Icon icon="ph:spinner-bold" className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                      <span>Mengunduh...</span>
                    </>
                  ) : (
                    <>
                      <Icon icon="ph:download-simple-bold" className="w-4 h-4 sm:w-5 sm:h-5" />
                      <span>Download Statistik</span>
                    </>
                  )}
                </button>
              )}
            </div>
          )}

          <p className="px-1 text-[11px] leading-relaxed text-gray-400">
            M · Main &nbsp; WR · Win rate &nbsp; Set · Set menang-kalah
            <br />
            Poin · Poin dapat-kebobolan
          </p>
        </div>
      )}

      {tab === 'mmr' && (
        <div className="space-y-3">
          <BadmintonTopMmrList items={topMmr} />
        </div>
      )}

      {canEdit && tab === 'games' && (
        <Button
          type="button"
          onClick={onTambahGame}
          className="fixed bottom-[calc(5.5rem+env(safe-area-inset-bottom,0px))] right-4 z-30 h-12 rounded-full bg-[#FC480D] px-5 text-sm font-bold text-white shadow-lg hover:bg-[#D83B08]"
        >
          <Icon icon="mdi:plus" className="h-5 w-5" />
          Tambah Game
        </Button>
      )}
    </div>
  );
}
